import React from 'react';
import { Link } from 'react-router-dom';
import {
    LightBulbIcon, ArrowRightIcon, CpuChipIcon, FireIcon, QuestionMarkCircleIcon,
    SparklesIcon, WrenchScrewdriverIcon,
    AcademicCapIcon, CheckCircleIcon, ArrowLeftIcon,
    HeartIcon, GlobeAltIcon, SunIcon, VideoCameraIcon
} from '@heroicons/react/24/solid';

const Bashar15MinutesAndCounting3 = ({ nextActivity, onProceed }) => {

    const youtubeVideoLink = "https://www.youtube.com/embed/Xllf_n0sJDI"; // Actual YouTube video link for Part 3

    return (
        <div className="w-full bg-gray-900 animate-fade-in">
            <div className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">

                <div className="bg-gray-900 p-6 rounded-xl border border-blue-500/20 mb-8">
                    <h1 className="text-3xl font-bold text-blue-400 mb-4 text-center">Bashar: Holotopia and Connection (Third Transmission)</h1>
                    <p className="text-gray-300 text-center">
                        This culminating part of the transmission focuses on the <span className="font-bold text-yellow-300">holotropic experience and meditation</span> to integrate knowledge into your neural network, activating your inner compass towards freedom and manifestation.
                    </p>
                </div>

                <div className="bg-gray-900 p-6 rounded-xl border border-red-500/20 mb-8 text-center">
                    <h2 className="text-2xl font-bold text-red-400 mb-4 flex items-center justify-center gap-2">
                        <VideoCameraIcon className="w-8 h-8 text-red-300"/>
                        Watch the Full Transmission
                    </h2>
                    <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                        <iframe
                            src={youtubeVideoLink}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                            className="absolute top-0 left-0 w-full h-full rounded-lg"
                        ></iframe>
                    </div>
                </div>

                <div className="bg-gray-900 p-6 rounded-xl border border-yellow-500/20 mb-8">
                    <h2 className="text-2xl font-bold text-yellow-400 mb-4 text-center">Holotropic Measurement: Burning in the New Pattern</h2>
                    <div className="flex flex-col items-center gap-4 text-center">
                        <CpuChipIcon className="w-10 h-10 text-yellow-300 mb-2"/>
                        <p className="text-gray-400">
                            This meditation is designed to <span className="font-bold text-green-300">"burn" the belief structure into your memory</span>, superimposing the hexagonal diagram of negative beliefs in your imagination.
                        </p>
                        <ArrowRightIcon className="w-8 h-8 text-white/30 my-1 rotate-90 sm:rotate-0" />
                        <p className="text-gray-400">
                            The goal is to reconfigure and <span className="font-bold text-orange-300">crystallize this new model</span> in your neurological network, in the center of your brain, allowing your receiver to tune more clearly to the higher frequency of your higher self.
                        </p>
                        <HeartIcon className="w-10 h-10 text-pink-300 mb-2"/>
                        <p className="text-gray-400">
                            Feel the <span className="font-bold text-pink-300">unconditional loving support</span> from the higher mind and all of creation, as you allow the hexagonal pattern to anchor in the corpus callosum, connecting to all aspects of your personality.
                        </p>
                    </div>
                </div>

                <div className="bg-gray-900 p-6 rounded-xl border border-purple-500/20 mb-8">
                    <h2 className="text-2xl font-bold text-purple-400 mb-4 text-center">Hyperconductivity: The Flow of Energy Between Minds</h2>
                    <div className="flex flex-col items-center gap-4 text-center">
                        <FireIcon className="w-10 h-10 text-purple-300 mb-2"/>
                        <p className="text-gray-400">
                            As the new neural pathways form and crystallize, they become <span className="font-bold text-pink-300">superconductive and hyperconductive</span>, allowing energy from your higher self to flow freely and feed back to the brain.
                        </p>
                        <ArrowRightIcon className="w-8 h-8 text-white/30 my-1 rotate-90 sm:rotate-0" />
                        <p className="text-gray-400">
                            This <span className="font-bold text-red-300">"starburst of light"</span> in the brain regulates the flow in both directions, communicating with all levels of your being and connecting you with your true infinite self.
                        </p>
                    </div>
                </div>

                <div className="bg-gray-900 p-6 rounded-xl border border-teal-500/20 mb-8">
                    <h2 className="text-2xl font-bold text-teal-400 mb-4 text-center">From the Illusion of Fear to the Freedom of Choice</h2>
                    <div className="flex flex-col items-center gap-4 text-center">
                        <QuestionMarkCircleIcon className="w-10 h-10 text-teal-300 mb-2"/>
                        <p className="text-gray-400">
                            Introduce your specific fears and challenges into the central hexagon of the template. Recognize how negative beliefs trick you into feeling fear, loneliness, and helplessness.
                        </p>
                        <ArrowRightIcon className="w-8 h-8 text-white/30 my-1 rotate-90 sm:rotate-0" />
                        <p className="text-gray-400">
                            But in that dark space, look for the <span className="font-bold text-cyan-300">"little spark of doubt"</span>. This spark becomes a crack in the structure of the negative belief, a light at the end of the tunnel.
                        </p>
                        <SparklesIcon className="w-10 h-10 text-cyan-300 mb-2"/>
                        <p className="text-gray-400">
                            Use that doubt to <span className="font-bold text-cyan-300">question the negative belief</span>, taking a step toward the light. Even if fear attacks, you will know it's a lie; you are eternal, infinite, and one with everything.
                        </p>
                    </div>
                </div>

                <div className="bg-gray-900 p-6 rounded-xl border border-orange-500/20 mb-8">
                    <h2 className="text-2xl font-bold text-orange-400 mb-4 text-center">Your New Default Setting</h2>
                    <ul className="list-disc list-inside space-y-3 text-gray-300">
                        <li><span className="font-bold text-white">Transparency:</span> By seeing the structure of the negative belief with "X-ray vision," you recognize its fallacy and how it dissolves into space. <WrenchScrewdriverIcon className="inline-block w-5 h-5 text-orange-300"/></li>
                        <li><span className="font-bold text-white">Crystallization of the Path:</span> This new pattern solidifies in your brain, becoming your <span className="font-bold text-yellow-300">default position</span> and a constant reminder that there is always a door to the light. <CheckCircleIcon className="inline-block w-5 h-5 text-orange-300"/></li>
                        <li><span className="font-bold text-white">Eternal Reminder:</span> Even if you choose to forget, the "echo" of this choice persists, reminding you of your freedom and that there is another choice. <AcademicCapIcon className="inline-block w-5 h-5 text-orange-300"/></li>
                        <li><span className="font-bold text-white">Using Doubt:</span> Doubt becomes a tool in your favor, exploiting the weak point of the negative belief to seek the light.</li>
                    </ul>
                </div>

                <div className="bg-gray-900 p-6 rounded-xl border border-green-500/20 mb-8">
                    <h2 className="text-2xl font-bold text-green-400 mb-4 text-center">Recap and the Wisdom of First Contact</h2>
                    <p className="text-gray-300 mb-4 text-center">
                        Bashar emphasizes the importance of the <span className="font-bold text-lime-300">15 minutes of neural plasticity</span> and the <span className="font-bold text-lime-300">clarification of your manifestation and motivation mechanisms</span>. He reminds you that you are a new person, capable of experiencing the immediacy of shifting from reality to reality.
                    </p>
                    <div className="flex flex-col items-center gap-4 text-center">
                         <GlobeAltIcon className="w-10 h-10 text-blue-300 mb-2"/>
                        <p className="text-gray-400">
                            He shares an inspiring story about his first contact training, where he absorbed experiences from other specialists and the consciousness of his own world. This story symbolizes how connecting with your "home world" and your whole self prepares you for new interactions and a deeper understanding of the <span className="font-bold text-blue-300">unity of creation</span>.
                        </p>
                        <SunIcon className="w-10 h-10 text-yellow-300 mb-2"/>
                        <p className="text-gray-400">
                            Communication occurs on multiple levels, and when interacting with others, you present not just your persona, but the <span className="font-bold text-yellow-300">entirety of your world's consciousness</span>.
                        </p>
                    </div>
                </div>


                <div className="mt-12 text-center">
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-6">
                        <Link to="/bashar/tools/15-min-count/2" className="inline-flex items-center gap-2 bg-gray-700 text-gray-300 font-bold py-3 px-6 rounded-lg hover:bg-gray-600 transition-colors">
                            <ArrowLeftIcon className="w-5 h-5"/>
                            Go to Part 2
                        </Link>
                        {nextActivity && (
                            <button
                                onClick={onProceed}
                                className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-colors"
                            >
                                Continue to the Next Activity
                                <ArrowRightIcon className="w-5 h-5"/>
                            </button>
                        )}
                        <Link to="/metaphysics/bashar" className="inline-flex items-center gap-2 bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-500 transition-colors">
                            Next Transmission
                            <ArrowRightIcon className="w-5 h-5"/>
                        </Link>
                    </div>

                    <div className="text-center mt-6">
                        <Link to="/courses/manifest" className="text-sm text-gray-500 hover:text-yellow-400 transition-colors">Back to Syllabus</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Bashar15MinutesAndCounting3;